// src/components/common/UserDropdown.js
import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ClipboardList, Ticket, Bell, Heart, Gift, User, LogOut } from 'lucide-react';
import { userService } from '../../services/userService';
import './UserDropdown.scss';

const UserDropdown = ({ user }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  // 외부 클릭 시 드롭다운 닫기
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // ESC 키로 닫기
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  const handleLogout = () => {
    userService.logout();
    setIsOpen(false);
    navigate('/');
    window.location.reload();
  };

  const handleMenuClick = () => {
    setIsOpen(false);
  };

  const menuItems = [
    { to: '/mypage', label: '내 예약', Icon: ClipboardList },
    { to: '/mypage/tickets', label: '내 티켓', Icon: Ticket },
    { to: '/mypage/notifications', label: '알림', Icon: Bell },
    { to: '/mypage/favorites', label: '찜한 이벤트', Icon: Heart },
    { to: '/mypage/coupons', label: '쿠폰함', Icon: Gift },
    { to: '/mypage/profile', label: '회원정보 수정', Icon: User },
  ];

  return (
    <div className="user-dropdown" ref={dropdownRef}>
      <button
        className={`user-dropdown__trigger ${isOpen ? 'user-dropdown__trigger--open' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <span className="user-dropdown__name">{user?.name || '마이페이지'}</span>
        <svg
          className="user-dropdown__arrow"
          viewBox="0 0 24 24"
          width="16"
          height="16"
          fill="currentColor"
        >
          <path d="M7 10l5 5 5-5z" />
        </svg>
      </button>

      {isOpen && (
        <div className="user-dropdown__menu" role="menu">
          {/* 사용자 정보 헤더 */}
          <div className="user-dropdown__header">
            <div className="user-dropdown__avatar">
              {user?.name?.charAt(0) || 'U'}
            </div>
            <div className="user-dropdown__info">
              <span className="user-dropdown__user-name">{user?.name}</span>
              <span className="user-dropdown__user-email">{user?.email}</span>
            </div>
          </div>

          <div className="user-dropdown__divider" />

          {/* 메뉴 아이템 */}
          <ul className="user-dropdown__list">
            {menuItems.map((item) => (
              <li key={item.to}>
                <Link
                  to={item.to}
                  className="user-dropdown__item"
                  onClick={handleMenuClick}
                  role="menuitem"
                >
                  <span className="user-dropdown__item-icon"><item.Icon size={16} strokeWidth={2} /></span>
                  <span className="user-dropdown__item-label">{item.label}</span>
                </Link>
              </li>
            ))}
          </ul>

          <div className="user-dropdown__divider" />

          {/* 로그아웃 */}
          <button
            className="user-dropdown__logout"
            onClick={handleLogout}
            role="menuitem"
          >
            <span className="user-dropdown__item-icon"><LogOut size={16} strokeWidth={2} /></span>
            <span className="user-dropdown__item-label">로그아웃</span>
          </button>
        </div>
      )}
    </div>
  );
};

export default UserDropdown;
